import React, {
  useEffect,
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Button } from "@crowd/ui-kit";
import { debounce } from "../../../utils";
import IdeaService from "../../../services/ideaService";
import styles from "./CompletedIdeasWrapper.module.scss";
import {
  useIsDesktop,
  useIsMobile,
  useIsTablet,
} from "../../../hooks/useMedia";
import FourColumnLoader from "../../presentational/Loaders/FourColumnLoader/FourColumnLoader";
import { GridBlock } from "../../presentational/GridBlock/GridBlock";
import {
  splitCardsIntoBlocks,
  CARD_LAYOUT_MAP,
  mapMediaCardToIdea,
} from "../../../utils/mediaCards.utils";
import { Idea } from "../../../types/Idea.interface";
import { Filter } from "../../pages/Ideas/IdeasFilter/IdeasFilter";
import MediaIdeaService from "../../../services/mediaIdeaService";

export interface CompletedIdeasWrapperHandle {
  loadPage: (appendToGrid?: boolean) => Promise<Idea[]>;
}

interface PageData {
  normalCards: Idea[];
  mediaCards: Idea[];
}

export const CompletedIdeasWrapper = forwardRef<
  CompletedIdeasWrapperHandle,
  {
    filter?: Filter;
    onCardClick?: (idea: Idea, pageIndex: number) => void;
    onAuthorClick?: (idea: Idea) => void;
    onTagClick?: (idea: Idea) => void | null;
    onLike?: (idea: Idea) => void;
    onLoadEnd?: (ideas: Idea[], pageIndex: number) => void;
    setIsLoading?: (isLoading: boolean) => void;
    isLoading?: boolean;
  }
>(
  (
    {
      onCardClick,
      onAuthorClick,
      onTagClick,
      onLike,
      onLoadEnd,
      filter,
      setIsLoading,
      isLoading,
    },
    ref
  ) => {
    const [pages, setPages] = useState<
      { normalCards: Idea[]; mediaCards: Idea[] }[]
    >([]);

    const pagesRenderedCardsRef = useRef<Idea[][]>([]);

    const [shouldLoadAfterReset, setShouldLoadAfterReset] = useState(false);

    const [normalBuffer, setNormalBuffer] = useState<Idea[]>([]);
    const [mediaBuffer, setMediaBuffer] = useState<Idea[]>([]);

    const pageNormalRef = useRef(0);
    const pageMediaRef = useRef(0);
    const isFirstMount = useRef(true);

    const [normalTotalPages, setNormalTotalPages] = useState<number | null>(
      null
    );
    const [mediaTotalPages, setMediaTotalPages] = useState<number | null>(null);

    // TO-DO: рефактор разделить функции load и prefetch(для подгрузки при перелистывании карточек внутри модалки)
    const prefetchBatchRef = useRef<PageData | null>(null);
    const prefetchPageRef = useRef<number | null>(null);

    const isDesktop = useIsDesktop();
    const isTablet = useIsTablet();
    const isMobile = useIsMobile();
    const deviceType = isDesktop ? "desktop" : isTablet ? "tablet" : "mobile";

    const layout = CARD_LAYOUT_MAP[deviceType];

    const isPhotoOnly =
      filter?.type?.value === "photo" || filter?.tags?.length > 0; // у медиа-карточек нет тэгов, поэтому запрашиваем только обычные
    const isVideoOnly = filter?.type?.value === "video";

    const loadPage = async (appendToGrid: boolean = true): Promise<Idea[]> => {
      if (isLoading) return [];
      setIsLoading(true);

      try {
        if (
          appendToGrid &&
          prefetchBatchRef.current &&
          prefetchPageRef.current === pageNormalRef.current
        ) {
          const { normalCards, mediaCards } = prefetchBatchRef.current;
          setPages((prev) => [...prev, { normalCards, mediaCards }]);
          setTimeout(() => {
            const flatOrdered = pagesRenderedCardsRef.current
              .slice(0, pageNormalRef.current + 1)
              .flat();
            onLoadEnd?.(flatOrdered, pageNormalRef.current);
          }, 0);

          prefetchBatchRef.current = null;
          prefetchPageRef.current = null;
          return [...normalCards, ...mediaCards];
        }

        const mergedNormal = [...normalBuffer];
        const mergedMedia = [...mediaBuffer];

        const needsNormal =
          mergedNormal.length < layout.count &&
          (normalTotalPages === null ||
            pageNormalRef.current < normalTotalPages);
        const needsMedia =
          mergedMedia.length < (isDesktop || isTablet ? 8 : 6) &&
          (mediaTotalPages === null || pageMediaRef.current < mediaTotalPages);

        const ideaParams = {
          sort: `${filter?.sort?.value ?? "pos"},desc`,
          page: pageNormalRef.current,
          size: isDesktop ? 16 : isTablet ? 12 : 6,
        };
        const mediaParams = {
          page: pageMediaRef.current,
          size: isDesktop || isTablet ? 8 : 6,
          sort: `${filter?.sort?.value ?? "position"},desc`,
        };
        const ideaFilter = {
          statuses: ["PUBLISHED", "PUBLISHED_MAIN"] as const,
          tags: filter?.tags ?? undefined,
          isRandom: false,
          hidden: false,
          searchQuery: filter?.search.length ? filter.search : undefined,
          district: filter?.district?.name ?? undefined,
        };

        if (!isVideoOnly && needsNormal) {
          const resp = await IdeaService.getByFilter(ideaParams, ideaFilter);
          mergedNormal.push(...(resp.data ?? []));
          setNormalTotalPages(resp.paging?.totalPages ?? null);
          pageNormalRef.current++;
        }

        if (!isPhotoOnly && needsMedia) {
          const resp = await MediaIdeaService.getByFilter(
            mediaParams,
            ideaFilter
          );
          mergedMedia.push(...(resp.data ?? []).map(mapMediaCardToIdea));
          setMediaTotalPages(resp.paging?.totalPages ?? null);
          pageMediaRef.current++;
        }

        const { top, bottom, remaining } = splitCardsIntoBlocks(
          mergedNormal,
          mergedMedia,
          deviceType
        );

        if (
          !top.normal.length &&
          !top.media.length &&
          !bottom.normal.length &&
          !bottom.media.length
        ) {
          return [];
        }

        setNormalBuffer(remaining.normal);
        setMediaBuffer(remaining.media);

        const pageData = {
          normalCards: [...top.normal, ...bottom.normal],
          mediaCards: [...top.media, ...bottom.media],
        };

        if (!appendToGrid) {
          prefetchBatchRef.current = pageData;
          prefetchPageRef.current = pageNormalRef.current;
        } else {
          setPages((prev) => [...prev, pageData]);
        }

        setTimeout(() => {
          const flatOrdered = pagesRenderedCardsRef.current
            .slice(0, pageNormalRef.current + 1)
            .flat();
          onLoadEnd?.(flatOrdered, pageNormalRef.current);
        }, 0);
        return [...pageData.normalCards, ...pageData.mediaCards];
      } catch (err) {
        console.error("Ошибка при loadPage:", err);
        return [];
      } finally {
        setIsLoading(false);
      }
    };

    useImperativeHandle(ref, () => ({
      loadPage,
    }));

    function isExhausted(
      totalPages: number | null,
      currentPage: number,
      buffer: any[]
    ): boolean {
      return (
        totalPages !== null &&
        currentPage + 1 > totalPages &&
        buffer.length === 0
      );
    }

    const showLoadMoreButton =
      // 1) Предзагруженная страница?
      !!prefetchBatchRef.current ||
      // 2) Видео-только
      (isVideoOnly
        ? !isExhausted(mediaTotalPages, pageMediaRef.current, mediaBuffer)
        : // 3) Фото-только
          isPhotoOnly
          ? !isExhausted(normalTotalPages, pageNormalRef.current, normalBuffer)
          : !(
              isExhausted(
                normalTotalPages,
                pageNormalRef.current,
                normalBuffer
              ) &&
              isExhausted(mediaTotalPages, pageMediaRef.current, mediaBuffer)
            ));

    const resetPage = () => {
      if (isFirstMount.current) {
        isFirstMount.current = false;
        return;
      }

      setPages([]);
      setNormalBuffer([]);
      setMediaBuffer([]);
      pageNormalRef.current = 0;
      pageMediaRef.current = 0;
      setNormalTotalPages(null);
      setMediaTotalPages(null);
      setShouldLoadAfterReset(true);
    };

    useEffect(() => {
      if (!shouldLoadAfterReset) return;

      const load = async () => {
        await loadPage();
        setShouldLoadAfterReset(false);
      };

      load();
    }, [shouldLoadAfterReset]);

    useEffect(() => {
      resetPage();
    }, [
      isDesktop,
      isTablet,
      isMobile,
      filter.district,
      filter.tags,
      filter.type,
      filter.sort,
    ]);

    useEffect(() => {
      const handler = debounce(() => {
        resetPage();
      }, 500);
      handler();
      return () => handler.cancel?.();
    }, [filter.search]);

    const renderPage = (
      normalCards: Idea[],
      mediaCards: Idea[],
      index: number
    ) => {
      const { top, bottom } = splitCardsIntoBlocks(
        normalCards,
        mediaCards,
        deviceType
      );

      return (
        <div key={index} className={styles.page}>
          <GridBlock
            normalCards={top.normal}
            mediaCards={top.media}
            type="top"
            onCardClick={(idea) => onCardClick?.(idea, index)}
            onLike={onLike}
            onTagClick={onTagClick}
            onAuthorClick={onAuthorClick}
            onRenderedCards={(cards) => {
              pagesRenderedCardsRef.current[index] = cards;
            }}
          />
          {!isMobile &&
            Boolean(bottom.media.length || bottom.normal.length) && (
              <GridBlock
                normalCards={bottom.normal}
                mediaCards={bottom.media}
                type="bottom"
                onCardClick={(idea) => onCardClick?.(idea, index)}
                onLike={onLike}
                onTagClick={onTagClick}
                onAuthorClick={onAuthorClick}
                onRenderedCards={(cards) => {
                  // объединяем с top
                  const prev = pagesRenderedCardsRef.current[index] || [];
                  pagesRenderedCardsRef.current[index] = [...prev, ...cards];
                }}
              />
            )}
        </div>
      );
    };

    return (
      <div className={styles.wrapper}>
        {isLoading && !pages.length && <FourColumnLoader isTitled={false} />}
        {pages.map(({ normalCards, mediaCards }, index) =>
          renderPage(normalCards, mediaCards, index)
        )}
        {showLoadMoreButton && !isLoading && (
          <Button
            size="m"
            onClick={loadPage}
            text="Показать еще"
            type="outlined"
            classNames={styles.btn}
          />
        )}
      </div>
    );
  }
);
